/**
 * Aspilon key ingredients, layout "Tabs" (blocks/aspilon-ingredients.liquid). WAI-ARIA tabs with automatic
 * activation: a click, or Left/Right (Up/Down), Home and End on the list, selects an ingredient and shows its card.
 * Roving tabindex keeps one tab in the Tab order. The list is hidden in the markup and shown here, so without
 * JavaScript every card stays visible, one under the other. No animation.
 */
class AspilonIngredientTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (!this.tablist || this.tabs.length === 0) return;

    this.querySelector('[data-ingredient-nav]')?.removeAttribute('hidden');
    const initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.tabs[0];
    this.#select(initial);

    this.tablist.addEventListener('click', this.#onClick);
    this.tablist.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.tablist?.removeEventListener('click', this.#onClick);
    this.tablist?.removeEventListener('keydown', this.#onKeydown);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement) this.#select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const current = this.tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (current === -1) return;

    const last = this.tabs.length - 1;
    const forward = getComputedStyle(this).direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = forward === 'ArrowRight' ? 'ArrowLeft' : 'ArrowRight';
    let next;
    if (event.key === forward || event.key === 'ArrowDown') next = current === last ? 0 : current + 1;
    else if (event.key === back || event.key === 'ArrowUp') next = current === 0 ? last : current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;

    event.preventDefault();
    const tab = this.tabs[next];
    if (!tab) return;
    this.#select(tab);
    tab.focus();
  };

  /** @param {HTMLElement} selected */
  #select(selected) {
    for (const tab of this.tabs) {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) panel.hidden = !isSelected;
    }
  }
}

if (!customElements.get('aspilon-ingredient-tabs')) {
  customElements.define('aspilon-ingredient-tabs', AspilonIngredientTabs);
}
